import i18n from 'src/locales/i18n';

import { TenantType } from 'src/types/enums';
import { PayPeriod } from 'src/types/payment';
import { BusinessErrorCodes } from 'src/types/business-errors';

import { Feature, Product, PriceTier, ProductType, Subscription } from './billing.types';

export const getActiveProduct = (
  subscription: Subscription,
  products: Product[]
): Product | undefined => {
  const productsToSearch = products.filter((product) => product.type === ProductType.BASE_PLAN);

  const subscriptionProductIds = subscription.products.map((subProduct) => subProduct.identifier);

  return productsToSearch.find((product) => subscriptionProductIds.includes(product.identifier));
};

/**
 * How a new seat total splits between an agency's seats on its old seat-band price and seats at
 * the current price. Mirrors the backend rule (ADR 0012): old-price seats are removed first and
 * added seats are charged at the current price.
 */
export const splitAgencySeats = (
  legacySeats: number,
  currentSeats: number,
  totalSeats: number
): { legacy: number; current: number } => {
  const legacy = Math.min(legacySeats, Math.max(0, totalSeats - currentSeats));

  return { legacy, current: Math.max(0, totalSeats - legacy) };
};

export const getTenantFreeProduct = (tenant: TenantType, products: Product[]): Product => {
  const productsToSearch = products.filter(
    (product) => product.type === ProductType.BASE_PLAN && product.tenant_type === tenant
  );

  return productsToSearch.find((product) => !product.subscription_required) as Product;
};

export const getProductPrice = (
  product: Product | undefined,
  payPeriod: PayPeriod
): number | undefined =>
  product ? product.prices.find((price) => price.period === payPeriod)?.amount : undefined;

export const getProductTiers = (
  product: Product | undefined,
  payPeriod: PayPeriod
): PriceTier[] | undefined =>
  product ? product.prices.find((price) => price.period === payPeriod)?.tiers : undefined;

export const getProductFeature = (
  product: Product | undefined,
  key: string
): Feature | undefined =>
  product ? product.features.find((feature) => feature.type === key) : undefined;

export const getPayInvoiceErrorMessage = (e: any): string => {
  if (e?.data?.error_code === BusinessErrorCodes.PaymentMethodError) {
    return i18n.t('billing.api.invoices.pay.declined');
  }

  if (e?.data?.error_code === BusinessErrorCodes.InvoiceInvalidState) {
    return i18n.t('billing.api.invoices.pay.invalid');
  }

  return i18n.t('billing.api.invoices.pay.default_error');
};

export const getSubscriptionErrorMessage = (e: any): string => {
  if (e?.data?.error_code === BusinessErrorCodes.PaymentMethodNotAttached) {
    return i18n.t('billing.api.subscription.upgrade.declined');
  }

  if (e?.data?.error_code === BusinessErrorCodes.NoSubscriptionRequired) {
    return i18n.t('billing.api.subscription.upgrade.no_subscription_required');
  }

  if (e?.data?.error_code === BusinessErrorCodes.InvalidPromoCode) {
    return i18n.t('billing.api.subscription.upgrade.invalid_promo_code');
  }

  if (e?.data?.error_code === BusinessErrorCodes.DiscountAlreadyApplied) {
    return i18n.t('billing.api.subscription.upgrade.discount_already_applied');
  }

  if (e?.data?.error_code === BusinessErrorCodes.ProductNotAvailable) {
    return i18n.t('billing.api.subscription.upgrade.product_not_available');
  }

  if (e?.data?.error_code === BusinessErrorCodes.LegacySeatsMonthlyOnly) {
    return i18n.t('billing.api.subscription.upgrade.legacy_seats_monthly_only');
  }

  if (e?.data?.error_code === BusinessErrorCodes.UnmatchedSubscriptionItems) {
    return i18n.t('billing.api.subscription.upgrade.unmatched_subscription_items');
  }

  if (e?.data?.error_code === BusinessErrorCodes.MarketingContactPacksMonthlyOnly) {
    return i18n.t('billing.api.subscription.upgrade.marketing_contact_packs_monthly_only');
  }

  if (e?.data?.error_code === BusinessErrorCodes.MarketingContactPacksBelowUsage) {
    const { used, minimum_packs, period_end } = e.data.context ?? {};
    return i18n.t('billing.api.subscription.upgrade.marketing_contact_packs_below_usage', { used, minimum_packs, period_end });
  }

  if (e?.data?.error_code === BusinessErrorCodes.MarketingContactUsageUnavailable) {
    return i18n.t('billing.api.subscription.upgrade.marketing_contact_usage_unavailable');
  }

  return i18n.t('billing.api.subscription.upgrade.default_error');
};

export const getIntentErrorMessage = (e: any) => {
  if (
    (e.data && e.data.error_code === BusinessErrorCodes.PaymentMethodError) ||
    e.code === 'card_declined'
  ) {
    return i18n.t('billing.api.card.create.intent.declined');
  }

  return i18n.t('billing.api.card.create.intent.default_error');
};

export const getConfirmPaymentErrorMessage = (e: any) => i18n.t('billing.api.confirm_payment.default_error');

